import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import BottomNavbar from "../components/BottomNavbar";
import { getRequest } from "../helpers/api.helper";

const money = (paise: number = 0) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(paise / 100);
const transactionLabels: Record<string, string> = {
  DEPOSIT: "Added to Wallet",
  CONTACT_VIEW_DEBIT: "Spent",
  CONTACT_VIEW_EARNING: "Earned",
  EARNINGS_PAYOUT: "Paid Earnings",
  ADMIN_ADJUSTMENT_CREDIT: "Added to Wallet",
  ADMIN_ADJUSTMENT_DEBIT: "Removed from Wallet",
};
const transactionFilters = [
  { value: "WALLET_ADDED", label: "Added to Wallet" },
  { value: "ADMIN_ADJUSTMENT_DEBIT", label: "Removed from Wallet" },
  { value: "CONTACT_VIEW_DEBIT", label: "Spent" },
  { value: "CONTACT_VIEW_EARNING", label: "Earned" },
  { value: "EARNINGS_PAYOUT", label: "Paid Earnings" },
];
type Transaction = {
  transactionId: string; createdAt: string; transactionType: string; maritalId?: string;
  amount: number; balanceBefore: number; balanceAfter: number; earningsBefore: number; earningsAfter: number;
  description: string; referenceId: string; performedBy: string; relatedBureauId?: string;
};


export default function WalletTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError("");
    const query = new URLSearchParams({ page: String(page), limit: "10", ...(type ? { type } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}) });
    try {
      const response = await getRequest(`/bureau/wallet/transactions?${query}`);
      if (sequence !== requestSequence.current) return;
      if (response.success && response.data) {
        setTransactions(response.data.transactions);
        setTotalPages(response.data.pagination.totalPages);
        setTotal(response.data.pagination.total);
      } else setError(response.message || "Unable to load transaction history.");
    } catch {
      if (sequence === requestSequence.current) setError("Unable to load transaction history.");
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [page, type, from, to]);

  useEffect(() => {
    void load();
    return () => { requestSequence.current += 1; };
  }, [load]);
  useEffect(() => {
    const refresh = () => { void load(); };
    window.addEventListener("bureau-wallet-updated", refresh);
    return () => window.removeEventListener("bureau-wallet-updated", refresh);
  }, [load]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="safe-area-top sticky top-0 z-40 border-b border-purple-100 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" aria-label="Back to dashboard" className="rounded-full p-2 text-purple-700 hover:bg-purple-50">
              <ArrowLeft size={22} aria-hidden="true" />
            </Link>
            <h1 className="text-lg font-bold sm:text-xl">Transaction History</h1>
          </div>
          <button type="button" onClick={() => void load()} disabled={loading} className="rounded-lg border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-700 disabled:opacity-50">{loading ? "Loading…" : "Refresh"}</button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 pb-32 sm:px-6">
        {error && <p role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      <section className="rounded-2xl border border-purple-100 bg-white p-4 shadow-sm sm:p-6" aria-label="Transaction History">
        <div className="my-3 flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-600">Transaction type
            <select value={type} onChange={(event) => { setType(event.target.value); setPage(1); }} className="mt-1 block rounded-lg border bg-white p-2 text-sm text-gray-800">
              <option value="">All transactions</option>
              {transactionFilters.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-600">From<input type="date" value={from} onChange={(event) => { setFrom(event.target.value); setPage(1); }} className="mt-1 block rounded-lg border p-2 text-sm text-gray-800" /></label>
          <label className="text-xs text-gray-600">To<input type="date" value={to} onChange={(event) => { setTo(event.target.value); setPage(1); }} className="mt-1 block rounded-lg border p-2 text-sm text-gray-800" /></label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-left text-sm">
            <thead className="bg-gray-50 text-gray-600"><tr>{["Date", "Type / Audit Details", "Profile ID", "Amount", "Wallet Balance", "Earnings"].map((label) => <th key={label} className="p-3 font-semibold">{label}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">
              {!loading && transactions.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-500">No transactions found.</td></tr>}
              {transactions.map((transaction) => {
                const debit = ["CONTACT_VIEW_DEBIT", "ADMIN_ADJUSTMENT_DEBIT", "EARNINGS_PAYOUT"].includes(transaction.transactionType);
                return (
                  <tr key={transaction.transactionId}>
                    <td className="whitespace-nowrap p-3">{new Date(transaction.createdAt).toLocaleString("en-IN")}</td>
                    <td className="max-w-sm p-3"><p className="font-medium">{transactionLabels[transaction.transactionType]}</p><p className="text-xs text-gray-500">{transaction.description}</p>
                      <details className="mt-1 text-xs text-gray-500"><summary className="cursor-pointer">Audit details</summary><p className="break-all">Transaction: {transaction.transactionId}</p><p className="break-all">Reference: {transaction.referenceId}</p><p>Performed by: {transaction.performedBy}</p>{transaction.relatedBureauId && <p>Related Bureau: {transaction.relatedBureauId}</p>}</details>
                    </td>
                    <td className="p-3">{transaction.maritalId || "—"}</td>
                    <td className={`whitespace-nowrap p-3 font-semibold ${debit ? "text-red-600" : "text-emerald-700"}`}>{debit ? "−" : "+"}{money(transaction.amount)}</td>
                    <td className="whitespace-nowrap p-3"><span className="text-xs text-gray-500">{money(transaction.balanceBefore)} → </span>{money(transaction.balanceAfter)}</td>
                    <td className="whitespace-nowrap p-3">{money(transaction.earningsAfter)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2 text-sm">
          <span className="text-gray-500">{total} transactions · Page {page} of {Math.max(1, totalPages)}</span>
          <div className="flex gap-2"><button type="button" disabled={loading || page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Previous</button><button type="button" disabled={loading || page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Next</button></div>
        </div>
      </section>
      </main>
      <BottomNavbar />
    </div>
  );
}
